import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'
import { CreateReportPayload, Report, ReportIndexType } from './types'

const API_URL = import.meta.env.VITE_EPC_API_URL ?? ''

// Re-export for backwards compatibility
export { ReportIndexType } from './types'

export const useCreateReportMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (variables: { payload: CreateReportPayload }) => {
      const { payload } = variables

      // Convert facetFilters from string array to object array
      const convertFacetFilters = (filters?: string[]) => {
        if (!filters || filters.length === 0) return []

        return filters.map((filter) => {
          // Parse "key:value" format into object
          const [key, value] = filter.split(':')
          return { [key]: value }
        })
      }

      // Convert camelCase to snake_case for API
      const apiPayload = {
        name: payload.name,
        description: payload.description,
        query_string: payload.queryString,
        facet_filters: convertFacetFilters(payload.facetFilters),
        index_type: payload.indexType,
      }

      const response = await fetchWithAuth(`${API_URL}/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(apiPayload),
      })

      return response as Report
    },
    onSuccess: () => {
      // Invalidate and refetch reports list
      queryClient.invalidateQueries({ queryKey: ['reports'] })
    },
  })

  return [
    async (variables: { variables: { payload: CreateReportPayload } }) => {
      return mutation.mutateAsync({ payload: variables.variables.payload })
    },
    { loading: mutation.isPending, error: mutation.error },
  ] as const
}
